import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

import GeneralLayout from "components/PageLayouts/GeneralLayout";
import GenericRecordSelection from "components/Records/RecordSelection/GenericRecordSelection";

import AddFlyerPopup from "./Flyers/AddFlyersPopup";
import FlyersRightDrawer from "./Flyers/FlyersRightDrawer";
import FlyersBottomDrawer from "./Flyers/FlyersBottomDrawer";
import FlyersDetail from "./Flyers/FlyersDetail";
import FlyerEmptyPage from "./Flyers/FlyersEmptyPage";

import { flyerAPI } from "api/Interactions/FlyersAPI";
import { Flyer } from "api/Interactions/InteractionsInterfaces";

const Flyers: React.FC = () => {
  const [flyersData, setFlyersData] = useState<Flyer[]>([]);
  const [selectedFlyer, setSelectedFlyer] = useState<Flyer | null>(null);
  const [loading, setLoading] = useState(true);

  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // ❗ Safe active ID (null instead of 0)
  const activeId =
    routeId ? Number(routeId) :
    searchParams.get("id") ? Number(searchParams.get("id")) :
    null;

  useEffect(() => {
    flyerAPI.getAll()
      .then(setFlyersData)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!activeId) {
      setSelectedFlyer(null);
      return;
    }

    flyerAPI.getById(activeId)
      .then(setSelectedFlyer)
      .catch(() => setSelectedFlyer(null));
  }, [activeId]);

  const handleSelectFlyer = (id: number) => {
    navigate(`/interactions/flyers/${id}`);
  };

  if (loading)
    return <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}><CircularProgress /></Box>;

  return (
    <GeneralLayout
      useLeftDrawer
      useRightDrawer
      useBottomDrawer
      defaultOpenLeft={true}
      defaultOpenRight={false}
      defaultOpenBottom={false}
      leftDrawerContent={
        <GenericRecordSelection<Flyer>
          items={flyersData}
          selectedId={activeId ?? undefined}
          onSelect={(f) => handleSelectFlyer(f.id)}
          AddPopupComponent={AddFlyerPopup}
          onCreated={(newFlyer) => {
            setFlyersData((prev) => [...prev, newFlyer]);
            navigate(`/interactions/flyers/${newFlyer.id}`);
          }}
          idKey="id"
          nameKey={(m) => m.name || `Flyer ${m.id}`}
          colorKey="interactionsPrimary"
          searchPlaceholder="Search flyers..."
        />
      }
      rightDrawerContent={activeId ? <FlyersRightDrawer flyerId={activeId} /> : null}
      bottomDrawerContent={activeId ? <FlyersBottomDrawer flyerId={activeId} /> : null}
    >
      {selectedFlyer ? (
        <FlyersDetail
          flyerId={activeId!}
          onClose={() => navigate("/interactions/flyers/")}
        />
      ) : (
        <FlyerEmptyPage
          onFlyersCreated={(newFlyer) => navigate(`/interactions/flyers/${newFlyer.id}`)}
        />
      )}
    </GeneralLayout>
  );
};

export default Flyers;
